import { capitalizeFirstLetter } from "helpers/common";
import useFlightSearchParams from "hooks/flight/useFlightSearchParams";
import useHotelSearchParams from "hooks/hotel/useHotelSearchParams";
import useApiClient from "hooks/useApiClient";
import useAutoComplete from "hooks/useAutoComplete";
import React, { useEffect, useState } from "react";
import { Offcanvas, OffcanvasHeader, OffcanvasBody } from "react-bootstrap";
import { FaArrowLeftLong } from "react-icons/fa6";
import { PiAirplaneInFlightLight } from "react-icons/pi";
import { SlLocationPin } from "react-icons/sl";



const mapFlightResponseToOptions = (data) => {
    return data.map((airport) => ({
      id: airport.id,
      city_name: airport.city_name,
      iata: airport.iata,
      country_name: airport.country_name,
      airport_name: airport.name ? airport.name : airport.city_name + " Airport",
    }));
  };
const mapHotelResponseToOptions = (data) => {
    return data.map((hotel) => ({
        id: hotel.id,
        description: hotel.description,
        descriptionEn: hotel.descriptionEn,
    }));
};

const mapTopWeightedAirportsToOptions = (data) => {
    return data.map((airport) => ({
        id: airport?.id ?? airport?.iata,
        city_name: airport?.city_name ?? airport?.city ?? "",
        iata: airport?.iata ?? airport?.id ?? "",
        country_name: airport?.country_name ?? airport?.country ?? "",
        airport_name:
            airport?.name ??
            airport?.airport_name ??
            ((airport?.city_name ?? airport?.city ?? "") + " Airport"),
    }));
};

const MobileAutoComplete = ({type,isOpen, onHide, placeholder, id ,paramName }) => {
    const { urls, api } = useApiClient();
    const { updateFlightSearchParams } = useFlightSearchParams();
    const { updateHotelSearchParams } = useHotelSearchParams();
    const {
        term,
        results,
        handleInputChange,
        loading,
        setResults,
      } = useAutoComplete({
          url: type === "flight" ? urls.flight.airportAutocomplete: urls.hotel.cityAutocomplete,
          mapResponseToOptions: type === "flight" ? mapFlightResponseToOptions : mapHotelResponseToOptions,
          });

    const [topAirportsCache, setTopAirportsCache] = useState(null);

    const fetchTopWeightedAirports = async (limit = 6) => {
        if (topAirportsCache && topAirportsCache.length > 0) {
            return topAirportsCache;
        }
        try {
            const res = await api.get(urls.flight.topWeightedAirports, {
                params: {
                    limit,
                },
            });
            const raw = Array.isArray(res?.data?.data)
                ? res.data.data
                : Array.isArray(res?.data)
                ? res.data
                : [];
            const options = mapTopWeightedAirportsToOptions(raw);
            setTopAirportsCache(options);
            return options;
        } catch (error) {
            console.error("Error fetching top weighted airports:", error);
            return [];
        }
    };

    const preloadTopAirports = async () => {
        if (type !== "flight") return;
        if (term.trim().length > 0) return;
        const options = await fetchTopWeightedAirports();
        setResults(options);
    };

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => document.getElementById(id)?.focus(), 100);
            preloadTopAirports();
        }
        // Only re-run when the panel opens, not on every keystroke that changes preloadTopAirports.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, id]);

    // Create an array of 50 elements for loading skeletons
    const loadingSkeletons = Array.from({ length: 50 });

    return (
        <Offcanvas
            placement="bottom"
            show={isOpen}
            onHide={onHide}
            className="h-screen"
            tabIndex={-1}
            id="offcanvasSidebar"
            aria-labelledby={`${id}Label`}
        >
            <OffcanvasHeader className="offcanvas-header p-0 py-1 px-2 border-b">
                <div className="w-full relative flex items-center gap-1">
                    <FaArrowLeftLong
                        size={18}
                        className="cursor-pointer mx-2"
                        onClick={onHide}
                    />
                    <input
                        type="text"
                        id={id}
                        className="border-none p-3 flex-1 focus:border-none focus:outline-none font-semibold"
                        placeholder={placeholder}
                        autoFocus
                        value={term}
                        onChange={(e) => handleInputChange(e.target.value)}
                    />
                </div>
            </OffcanvasHeader>
            <OffcanvasBody className="offcanvas-body flex-column p-0 ">
                <div className="flex flex-col ">
                    {
                        loading && (
                            loadingSkeletons.map((_, index) => (
                                <div key={index} className="w-full mb-1 h-16 animate-pulse bg-gray-200 rounded"></div>
                            ))
                        )
                    }
                    {results.map((obj) => (
                        type === "flight" ? (
                            <div
                                key={obj.id}
                                onClick={() => { updateFlightSearchParams(paramName,obj) ; onHide(); }}  
                                className="flex cursor-pointer  items-center justify-between p-3 border-b  hover:bg-hover-color">
                                <div className="flex items-center justify-center gap-2 ">
                                <PiAirplaneInFlightLight size={25} stroke="2" />
                                <div className="">
                                    <div className="font-semibold text-md ">{obj.airport_name || obj.city_name + " Airport"}</div>
                                    <div className="text-sm text-paragraph-color">                        {
                                        obj?.country_name ?
                                        capitalizeFirstLetter(
                                            obj.country_name.toLowerCase(),
                                        ) :
                                        obj?.city_name
                                        }
                                    </div>
                                </div>
                                </div>
                                <div className="font-semibold text-md ms-1">
                                    {obj.iata}
                                </div>
                            </div>
                        ):(
                            <div 
                                onClick={() => { updateHotelSearchParams(paramName,obj) ; onHide(); }}
                                key={obj.id} 
                                className="flex items-center gap-2 p-3 border-b cursor-pointer hover:bg-hover-color">
                                <SlLocationPin size={20} />
                                <div className="">
                                <div className="font-semibold text-md ">{obj.description.split("-")[0]}</div>
                                <div className="text-sm text-paragraph-color">{obj.description}</div>
                                </div>
                            </div>
                        )
                    ))}
                </div>
            </OffcanvasBody>
        </Offcanvas>
    );
};

export default MobileAutoComplete;
